const SESSION_INDEX_KEY = "mcp-chat-sessions";
const CHAT_HISTORY_LIMIT = 7;

export function generateSessionId() {
    return Math.random().toString(36).substring(2, 30);
}

function isValidSessionId(sessionId) {
    return !!sessionId && sessionId !== "null" && sessionId !== "undefined";
}

function safeParseJSON(value, fallback) {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
}

function buildLocalTitle(history = []) {
    const firstUserMessage = history.find((message) => message?.role === "user" && message?.content);
    const title = firstUserMessage?.content || "New Conversation";
    return title.length > 70 ? `${title.substring(0, 70)}...` : title;
}

function updateSessionIndex(sessionId, history = [], options = {}) {
    if (!isValidSessionId(sessionId)) return;

    const parsedSessions = safeParseJSON(localStorage.getItem(SESSION_INDEX_KEY), []);
    const sessions = Array.isArray(parsedSessions) ? parsedSessions : [];
    const existingSessionIndex = sessions.findIndex((session) => session.sessionId === sessionId);
    const existingSession = existingSessionIndex >= 0 ? sessions[existingSessionIndex] : null;
    const lastMessage = history[history.length - 1];
    const now = new Date().toISOString();
    const shouldTouch = options.touch !== false;

    const nextSession = {
        sessionId,
        title: existingSession?.title || buildLocalTitle(history),
        preview: lastMessage?.content || "",
        messageCount: history.length,
        createdAt: options.createdAt || existingSession?.createdAt || now,
        updatedAt: shouldTouch ? now : (options.updatedAt || existingSession?.updatedAt || now),
    };

    let nextSessions;
    if (shouldTouch) {
        nextSessions = [
            nextSession,
            ...sessions.filter((session) => session.sessionId !== sessionId),
        ];
    } else if (existingSessionIndex >= 0) {
        nextSessions = sessions.slice();
        nextSessions[existingSessionIndex] = nextSession;
    } else {
        nextSessions = [...sessions, nextSession].sort((a, b) => {
            return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
        });
    }

    localStorage.setItem(SESSION_INDEX_KEY, JSON.stringify(nextSessions.slice(0, CHAT_HISTORY_LIMIT)));
}

export function getStoredSessionId() {
    const sessionId = localStorage.getItem("sessionId");
    return isValidSessionId(sessionId) ? sessionId : null;
}

export function setStoredSessionId(sessionId) {
    if (!isValidSessionId(sessionId)) {
        localStorage.removeItem("sessionId");
        return null;
    }

    localStorage.setItem("sessionId", sessionId);
    return sessionId;
}

export function getOrCreateSessionId() {
    const existingSessionId = getStoredSessionId();
    if (existingSessionId) {
        return existingSessionId;
    }

    return setStoredSessionId(generateSessionId());
}
export function createAISessionId() {
    return Math.random().toString(36).substring(2, 30);
}

export function getSessionHistory(sessionId) {
    const item = localStorage.getItem(`history-${sessionId}`);
    return safeParseJSON(item, []);
}

export function saveSessionHistory(sessionId, history, options = {}) {
    if (!isValidSessionId(sessionId)) return;
    if (!Array.isArray(history) || history.length === 0) return;

    localStorage.setItem(`history-${sessionId}`, JSON.stringify(history));
    updateSessionIndex(sessionId, history, options);
}

export function getLocalChatSessions() {
    const sessions = safeParseJSON(localStorage.getItem(SESSION_INDEX_KEY), []);
    return (Array.isArray(sessions) ? sessions : [])
        .filter((session) => isValidSessionId(session?.sessionId))
        .slice(0, CHAT_HISTORY_LIMIT);
}

export function createAwarenessManagerPath(sessionId, search = "") {
    const safeSessionId = sessionId || getOrCreateSessionId();
    const params = new URLSearchParams((search || "").replace(/^\?/, ""));
    params.set("sessionId", safeSessionId);
    return `/awareness-manager?${params.toString()}`;
}
